import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Send, Search, ChevronDown, ChevronUp, Sparkles, Clock, MapPin, Loader2, Lock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { usePreferences } from '../context/PreferencesContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const matchTagConfig = {
  exact: { label: 'Exact Match', className: 'bg-green-100 text-green-800 border-green-200' },
  related: { label: 'Related', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  might_help: { label: 'Might Help', className: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const ResultCard = ({ result, isExpanded, onToggle }) => {
  const tag = matchTagConfig[result.match_tag] || matchTagConfig.might_help;

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-md border-l-4 ${
        result.match_tag === 'exact' ? 'border-l-green-500' :
        result.match_tag === 'related' ? 'border-l-blue-500' : 'border-l-gray-300'
      }`}
      onClick={onToggle}
      data-testid={`result-card-${result.id}`}
    >
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 leading-relaxed">
              {result.question_text}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="outline" className={`text-xs ${tag.className}`}>
                {tag.label}
              </Badge>
              {/* Source-type badge — helps users know whether this
                  question came from the Oral bank or the Written papers.
                  Written papers just landed in ASK's index; more sources
                  (Notes, Forum, Live Class Archive) coming later. */}
              {result.source_type === 'written' ? (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300" data-testid={`result-source-badge-${result.id}`}>
                  Written
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-300" data-testid={`result-source-badge-${result.id}`}>
                  Oral
                </Badge>
              )}
              {result.grade && (
                <span className="text-xs text-gray-500">{result.grade}</span>
              )}
              {result.function_name && (
                <span className="text-xs text-gray-500">{result.function_name}</span>
              )}
              {result.mmd_location && (
                <span className="text-xs text-gray-400 flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" />{result.mmd_location}
                </span>
              )}
              {result.exam_month && (
                <span className="text-xs text-gray-400 flex items-center gap-0.5">
                  <Clock className="w-3 h-3" />{result.exam_month}
                </span>
              )}
              {result.marks != null && result.source_type === 'written' && (
                <span className="text-xs text-gray-500">{result.marks} marks</span>
              )}
              {result.asked > 1 && (
                <span className="text-xs text-amber-600">Asked {result.asked}x</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-400">{result.similarity_score}%</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {isExpanded && result.answer_text && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-1">Answer</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {result.answer_text}
            </p>
          </div>
        )}
        {isExpanded && !result.answer_text && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm text-gray-400 italic">No answer available for this question.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AskPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getModuleStatus, isModuleActive, loading: moduleLoading } = useModuleAccess();
  const { preferences } = usePreferences();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deepSearching, setDeepSearching] = useState(false);
  const [deepSearchStatus, setDeepSearchStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [otherGradesExpanded, setOtherGradesExpanded] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const userGrade = preferences?.grade;
  const askStatus = getModuleStatus('ask_semantic_search');
  const askActive = isModuleActive('ask_semantic_search');

  useEffect(() => {
    if (askActive) fetchDeepSearchStatus();
  }, [askActive]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchDeepSearchStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/ask/deep-search/status`, { headers });
      setDeepSearchStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch deep search status:', err);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q || q.length < 3 || loading) return;

    // Add user message
    const userMsg = { type: 'user', text: q, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setQuery('');
    setLoading(true);
    setExpandedId(null);

    try {
      const res = await axios.post(`${API}/api/ask/search`, { query: q }, { headers });
      if (res.data.requires_grade) {
        const promptMsg = {
          type: 'requires_grade',
          text: res.data.message || 'Please set your grade in Profile to use Ask.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, promptMsg]);
      } else {
        const resultMsg = {
          type: 'results',
          query: q,
          results: res.data.results || [],
          total: res.data.total || 0,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, resultMsg]);
      }
    } catch (err) {
      const errorMsg = {
        type: 'error',
        text: 'Failed to search. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleDeepSearch = async (originalQuery) => {
    if (deepSearching) return;
    setDeepSearching(true);

    // Add "searching" message
    const searchingMsg = {
      type: 'deep_search_loading',
      text: 'Processing, please wait...',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, searchingMsg]);

    try {
      const res = await axios.post(`${API}/api/ask/deep-search`, {
        query: originalQuery,
      }, { headers });

      // Remove loading message
      setMessages(prev => prev.filter(m => m.type !== 'deep_search_loading'));

      const data = res.data;

      if (!data.available) {
        setMessages(prev => [...prev, {
          type: 'deep_search_unavailable',
          text: data.message,
          timestamp: new Date(),
        }]);
      } else if (data.rate_limited) {
        setMessages(prev => [...prev, {
          type: 'deep_search_limited',
          text: data.message,
          timestamp: new Date(),
        }]);
      } else if (data.error) {
        setMessages(prev => [...prev, {
          type: 'error',
          text: data.message,
          timestamp: new Date(),
        }]);
      } else {
        setMessages(prev => [...prev, {
          type: 'deep_search_result',
          answer: data.answer,
          sources: data.sources || [],
          remaining: data.remaining,
          timestamp: new Date(),
        }]);
        fetchDeepSearchStatus();
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.type !== 'deep_search_loading'));
      setMessages(prev => [...prev, {
        type: 'error',
        text: 'Failed to connect to AI service.',
        timestamp: new Date(),
      }]);
    } finally {
      setDeepSearching(false);
    }
  };

  return (
    <Layout>
      {/* Module disabled/coming soon state */}
      {!moduleLoading && !askActive ? (
        <div className="page-transition max-w-3xl mx-auto flex flex-col items-center justify-center" style={{ height: 'calc(100vh - 180px)' }}
          data-testid="ask-page-disabled">
          <div className="text-center px-4">
            <div className="p-4 bg-gray-100 rounded-full mb-4 inline-flex">
              <Lock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              {askStatus === 'coming_soon' ? 'Ask is Coming Soon' : 'Ask is Currently Unavailable'}
            </h2>
            <p className="text-sm text-gray-500 max-w-md">
              {askStatus === 'coming_soon'
                ? 'The AI-powered semantic search feature is being prepared. Stay tuned!'
                : 'This feature is currently disabled. Please contact an administrator for more information.'}
            </p>
          </div>
        </div>
      ) : (
      <div
        className="page-transition mx-auto flex flex-col w-full max-w-4xl px-4 sm:px-6"
        style={{ minHeight: 'calc(100dvh - 140px)', maxHeight: 'calc(100dvh - 80px)' }}
        data-testid="ask-page"
      >

        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="p-2 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex-shrink-0">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900" data-testid="ask-title">Ask</h1>
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-amber-100 text-amber-800 border border-amber-300"
                  data-testid="ask-beta-badge"
                  title="Ask is still under active development — we're indexing more sources (notes, forum answers, live class archives) over the coming weeks."
                >
                  Beta
                </span>
              </div>
              <p className="text-sm text-gray-500">Search the question bank using AI-powered semantic matching</p>
            </div>
          </div>
          {deepSearchStatus && (
            <div className="mt-2 text-xs text-gray-400">
              {deepSearchStatus.enabled
                ? `Deep Search: ${deepSearchStatus.remaining}/${deepSearchStatus.daily_limit} remaining today`
                : ''}
            </div>
          )}
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto space-y-4 pb-4 min-h-0" data-testid="chat-area">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="p-4 bg-violet-50 rounded-full mb-4">
                <Search className="w-10 h-10 text-violet-400" />
              </div>
              <h2 className="text-lg font-medium text-gray-700 mb-2">What would you like to find?</h2>
              <p className="text-sm text-gray-400 max-w-md">
                Type any question or topic — the AI will find semantically similar questions from the database, even if the wording is different.
              </p>
              {!userGrade && (
                <div
                  className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left max-w-md"
                  data-testid="ask-set-grade-nudge"
                >
                  <p className="text-sm text-amber-900 font-medium mb-1">Set your grade first</p>
                  <p className="text-xs text-amber-800 mb-2">
                    Ask scopes results to your grade for relevance. Pick a grade in Profile → Preferences before searching.
                  </p>
                  <button
                    onClick={() => navigate('/profile')}
                    className="text-xs font-medium text-amber-900 underline hover:no-underline"
                    data-testid="ask-set-grade-btn"
                  >
                    Open Profile →
                  </button>
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {['What is OWS?', 'Explain SOLAS', 'Turbocharger maintenance'].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => { setQuery(suggestion); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-gray-200 text-gray-500 hover:bg-violet-50 hover:text-violet-600 hover:border-violet-200 transition-colors"
                    data-testid={`suggestion-${suggestion.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.type === 'user' && (
                <div className="flex justify-end">
                  <div className="bg-violet-600 text-white px-4 py-2.5 rounded-2xl rounded-br-sm max-w-[80%] text-sm">
                    {msg.text}
                  </div>
                </div>
              )}

              {msg.type === 'results' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-400 px-1">
                    {msg.total > 0
                      ? `Found ${msg.total} matching question${msg.total !== 1 ? 's' : ''}`
                      : 'No matching questions found in the database'}
                  </p>
                  {(() => {
                    if (msg.results.length === 0) return null;

                    // Split results: user's grade first, then others
                    const myGradeResults = userGrade
                      ? msg.results.filter(r => r.grade === userGrade)
                      : msg.results;
                    const otherResults = userGrade
                      ? msg.results.filter(r => r.grade !== userGrade)
                      : [];

                    const isOtherExpanded = otherGradesExpanded[idx] === true; // default collapsed

                    return (
                      <>
                        {/* User's grade results */}
                        {myGradeResults.length > 0 && (
                          <>
                            {userGrade && otherResults.length > 0 && (
                              <p className="text-xs font-medium text-violet-600 px-1">
                                Your grade — {userGrade} ({myGradeResults.length})
                              </p>
                            )}
                            {myGradeResults.map(result => (
                              <ResultCard
                                key={result.id}
                                result={result}
                                isExpanded={expandedId === result.id}
                                onToggle={() => setExpandedId(expandedId === result.id ? null : result.id)}
                              />
                            ))}
                          </>
                        )}

                        {/* Other grades results */}
                        {otherResults.length > 0 && (
                          <div className="pt-1">
                            <button
                              onClick={() => setOtherGradesExpanded(prev => ({ ...prev, [idx]: !isOtherExpanded }))}
                              className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 px-1 py-1 transition-colors"
                              data-testid="other-grades-toggle"
                            >
                              {isOtherExpanded ? (
                                <ChevronUp className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronDown className="w-3.5 h-3.5" />
                              )}
                              From other grades ({otherResults.length})
                            </button>
                            {isOtherExpanded && (
                              <div className="space-y-3 mt-2">
                                {otherResults.map(result => (
                                  <ResultCard
                                    key={result.id}
                                    result={result}
                                    isExpanded={expandedId === result.id}
                                    onToggle={() => setExpandedId(expandedId === result.id ? null : result.id)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* No results for user's grade */}
                        {userGrade && myGradeResults.length === 0 && otherResults.length > 0 && (
                          <p className="text-xs text-gray-400 italic px-1">
                            No results for {userGrade} — showing results from other grades
                          </p>
                        )}
                      </>
                    );
                  })()}
                  {/* Find Deeper button */}
                  <div className="flex justify-center pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeepSearch(msg.query)}
                      disabled={deepSearching}
                      className="text-violet-600 border-violet-200 hover:bg-violet-50"
                      data-testid="find-deeper-btn"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      Find Deeper
                    </Button>
                  </div>
                </div>
              )}

              {msg.type === 'deep_search_loading' && (
                <div className="flex items-center gap-2 px-4 py-3 bg-violet-50 rounded-lg text-sm text-violet-700">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {msg.text}
                </div>
              )}

              {msg.type === 'deep_search_unavailable' && (
                <div className="px-4 py-3 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-200">
                  <Sparkles className="w-4 h-4 inline mr-1.5 text-violet-400" />
                  {msg.text}
                </div>
              )}

              {msg.type === 'deep_search_limited' && (
                <div className="px-4 py-3 bg-amber-50 rounded-lg text-sm text-amber-700 border border-amber-200">
                  {msg.text}
                </div>
              )}

              {msg.type === 'deep_search_result' && (
                <Card className="border-l-4 border-l-violet-500 bg-violet-50/30">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                      <span className="text-xs font-medium text-violet-600">AI Deep Search</span>
                      {msg.remaining !== undefined && (
                        <span className="text-xs text-gray-400 ml-auto">{msg.remaining} remaining today</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {msg.answer}
                    </p>
                    {msg.sources?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-violet-100">
                        <p className="text-xs text-gray-400">Sources: {msg.sources.join(', ')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {msg.type === 'error' && (
                <div className="px-4 py-3 bg-red-50 rounded-lg text-sm text-red-600 border border-red-200">
                  {msg.text}
                </div>
              )}

              {msg.type === 'requires_grade' && (
                <div
                  className="px-4 py-3 bg-amber-50 rounded-lg text-sm text-amber-800 border border-amber-200 flex items-start gap-2"
                  data-testid="ask-requires-grade-banner"
                >
                  <Sparkles className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium mb-1">Grade required</p>
                    <p className="text-amber-700">{msg.text}</p>
                    <a
                      href="/profile"
                      className="inline-block mt-2 text-xs font-medium text-amber-900 underline hover:no-underline"
                      data-testid="ask-requires-grade-profile-link"
                    >
                      Go to Profile →
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              <span className="text-sm text-gray-500">Searching...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="border-t pt-3">
          <form onSubmit={handleSearch} className="flex gap-2" data-testid="ask-form">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask a question..."
              className="flex-1"
              disabled={loading}
              data-testid="ask-input"
              autoFocus
            />
            <Button
              type="submit"
              disabled={loading || query.trim().length < 3}
              className="bg-violet-600 hover:bg-violet-700 px-4"
              data-testid="ask-submit-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
      )}
    </Layout>
  );
};

export default AskPage;
